import React, { useEffect, useState } from 'react';
import { ProviderEvidence, DocumentViewer, ProviderDocumentUrl, Spinner, formatDate } from '@enter/core';
import { BaseButton } from '@enter/core';
import { inject, observer } from 'mobx-react';
import { IInjectedRootStore } from 'stores';
import * as S from './evidence-view-dialog.styles';

interface IEvidenceViewDialogProps {
    evidence: ProviderEvidence;
    onClose: () => void;
}

type IInjectedProps = IEvidenceViewDialogProps & IInjectedRootStore;

export const EvidenceViewDialog = inject('rootStore')(
    observer((props: IEvidenceViewDialogProps) => {
        const { evidence, onClose, rootStore } = props as IInjectedProps;
        const [documentLoading, setDocumentLoading] = useState(false);
        const [documentUrl, setDocumentUrl] = useState<ProviderDocumentUrl | undefined>();


        // Load document when component mounts
        useEffect(() => {
            if (!evidence.documentId || !rootStore) {
                return;
            }

            const loadDocument = async () => {
                setDocumentLoading(true);
                try {
                    const url = await rootStore.claimStore.loadDocumentDownloadUrl(evidence.documentId!);
                    setDocumentUrl(url);
                } catch (error) {
                    console.error('Error loading document:', error);
                } finally {
                    setDocumentLoading(false);
                }
            };

            loadDocument();
        }, [evidence.documentId, rootStore]);

        const isDenied = evidence.status === 'DENIED';
        const isApproved = evidence.status === 'APPROVED';

        if (!rootStore) {
            return <></>;
        }

        return (
        <S.DialogContainer>
            <S.Content>
                <S.FieldGroup>
                    <S.Field>
                        <S.FieldLabel>Type</S.FieldLabel>
                        <S.FieldValue>{evidence.evidenceType?.name || '-'}</S.FieldValue>
                    </S.Field>
                    
                    <S.Field>
                        <S.FieldLabel>Description</S.FieldLabel>
                        <S.FieldValue>{evidence.evidenceType?.description || '-'}</S.FieldValue>
                    </S.Field>
                    
                    <S.Field>
                        <S.FieldLabel>Status</S.FieldLabel>
                        <S.StatusBadge status={evidence.status || ''}>
                            {evidence.status || '-'}
                        </S.StatusBadge>
                    </S.Field>
                    
                    {isDenied && evidence.denyReason && (
                        <S.Field>
                            <S.FieldLabel>Deny Reason</S.FieldLabel>
                            <S.FieldValue>{evidence.denyReason}</S.FieldValue>
                        </S.Field>
                    )}
                    
                    {isDenied && evidence.denied && (
                        <S.Field>
                            <S.FieldLabel>Deny Date</S.FieldLabel>
                            <S.FieldValue>{formatDate(evidence.denied, 'MMM dd, yyyy')}</S.FieldValue>
                        </S.Field>
                    )}
                    
                    {isApproved && evidence.approved && (
                        <S.Field>
                            <S.FieldLabel>Approved Date</S.FieldLabel>
                            <S.FieldValue>{formatDate(evidence.approved, 'MMM dd, yyyy')}</S.FieldValue>
                        </S.Field>
                    )}
                    
                    <S.Field>
                        <S.FieldLabel>Notes</S.FieldLabel>
                        <S.FieldValue>{evidence.notes || '-'}</S.FieldValue>
                    </S.Field>
                </S.FieldGroup>
                
                {evidence.documentId && (
                    <S.DocumentSection>
                        <S.FieldLabel>Document</S.FieldLabel>
                        <S.DocumentViewer>
                            {documentLoading ? (
                                <S.DocumentPlaceholder>
                                    <Spinner />
                                    <br />
                                    <small>Loading document...</small>
                                </S.DocumentPlaceholder>
                            ) : documentUrl?.url ? (
                                <DocumentViewer url={documentUrl.url} />
                            ) : (
                                <S.DocumentPlaceholder>
                                    Document ID: {evidence.documentId}
                                    <br />
                                    <small>Unable to load document</small>
                                </S.DocumentPlaceholder>
                            )}
                        </S.DocumentViewer>
                    </S.DocumentSection>
                )}
            </S.Content>
            
            <S.Actions>
                <BaseButton
                    variant="outlined"
                    onClick={onClose}
                >
                    Close
                </BaseButton>
            </S.Actions>
        </S.DialogContainer>
        );
    }),
);

